import { useState, useCallback } from "react";
import { generatePassword } from "../../services/credentialService.js";

// ─────────────────────────────────────────────
//  Force du mot de passe
// ─────────────────────────────────────────────
function getStrength(pwd) {
  if (!pwd) return { score: 0, label: "—", color: "#555" };
  let s = 0;
  if (pwd.length >= 8)           s++;
  if (pwd.length >= 12)          s++;
  if (pwd.length >= 16)          s++;
  if (/[A-Z]/.test(pwd))         s++;
  if (/[0-9]/.test(pwd))         s++;
  if (/[^A-Za-z0-9]/.test(pwd))  s++;
  const c = Math.min(s, 5);
  return [
    { label:"Très faible", color:"#fa5252" },
    { label:"Faible",      color:"#ff922b" },
    { label:"Moyen",       color:"#ffd43b" },
    { label:"Fort",        color:"#69db7c" },
    { label:"Très fort",   color:"#40c057" },
    { label:"Excellent",   color:"#12b886" },
  ][c];
}

// ─────────────────────────────────────────────
//  Toggle switch
// ─────────────────────────────────────────────
function Toggle({ checked, onChange, label }) {
  return (
    <label style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer", userSelect:"none" }}>
      <div onClick={() => onChange(!checked)} style={{
        width:42, height:24, borderRadius:12, position:"relative",
        background: checked ? "#3D6B6B" : "#ccc",
        transition:"background .2s", flexShrink:0, cursor:"pointer",
      }}>
        <div style={{
          position:"absolute", top:3, left: checked ? 21 : 3,
          width:18, height:18, borderRadius:"50%", background:"#fff",
          transition:"left .2s", boxShadow:"0 1px 4px rgba(0,0,0,.25)",
        }}/>
      </div>
      <span style={{ fontSize:14, color:"var(--text-1)", fontWeight:500 }}>{label}</span>
    </label>
  );
}

// ─────────────────────────────────────────────
//  MODAL
//
//  Props :
//   - onClose     : fermer la modal
//   - onUse       : (optionnel) injecter le mot de passe dans un champ parent
//   - companyId   : (optionnel) pour la partie admin — passe l'id de l'entreprise
// ─────────────────────────────────────────────
export default function PasswordGeneratorModal({ onClose, onUse, companyId }) {
  const [password,   setPassword]   = useState("");
  const [length,     setLength]     = useState(18);
  const [useLower,   setUseLower]   = useState(true);
  const [useUpper,   setUseUpper]   = useState(true);
  const [useNumbers, setUseNumbers] = useState(true);
  const [useSymbols, setUseSymbols] = useState(true);
  const [loading,    setLoading]    = useState(false);
  const [copied,     setCopied]     = useState(false);
  const [error,      setError]      = useState("");

  const strength = getStrength(password);

  // Génération locale quand pas de companyId
  const generateLocally = (len, lower, upper, numbers, symbols) => {
    const sets = [
      lower   ? "abcdefghijklmnopqrstuvwxyz" : "",
      upper   ? "ABCDEFGHIJKLMNOPQRSTUVWXYZ" : "",
      numbers ? "0123456789" : "",
      symbols ? "!@#$%^&*()_+-=[]{}|;:,.<>?" : "",
    ].filter(Boolean);
    if (!sets.length) return "";
    const all = sets.join("");
    // Garantir au moins un caractère de chaque set
    let pwd = sets.map(s => s[Math.floor(Math.random() * s.length)]).join("");
    for (let i = pwd.length; i < len; i++) {
      pwd += all[Math.floor(Math.random() * all.length)];
    }
    // Mélanger
    return pwd.split("").sort(() => Math.random() - 0.5).join("");
  };

  const generate = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      let pwd;
      if (companyId) {
        // Admin avec entreprise → API backend (respecte la politique)
        const res = await generatePassword(companyId, length);
        pwd = res?.data ?? res;
      } else {
        // Pas d'entreprise → génération locale
        pwd = generateLocally(length, useLower, useUpper, useNumbers, useSymbols);
      }
      setPassword(typeof pwd === "string" ? pwd : String(pwd));
      setCopied(false);
    } catch {
      // Fallback local si l'API échoue
      const pwd = generateLocally(length, useLower, useUpper, useNumbers, useSymbols);
      if (pwd) {
        setPassword(pwd);
        setCopied(false);
      } else {
        setError("Activez au moins une option.");
      }
    } finally {
      setLoading(false);
    }
  }, [length, companyId, useLower, useUpper, useNumbers, useSymbols]);

  const handleCopy = () => {
    if (!password) return;
    navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleUse = () => {
    if (!password || !onUse) return;
    onUse(password);
    onClose();
  };

  return (
    <div
      style={{ position:"fixed", inset:0, zIndex:1060, background:"rgba(0,0,0,.5)", backdropFilter:"blur(4px)", display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{ background:"var(--card-bg)", borderRadius:16, width:"100%", maxWidth:420, boxShadow:"0 24px 80px rgba(0,0,0,.25)", overflow:"hidden", animation:"pgSlideUp .22s cubic-bezier(.34,1.56,.64,1)" }}>

        {/* En-tête */}
        <div style={{ padding:"20px 24px 16px", borderBottom:"1px solid var(--border)", position:"relative" }}>
          <h3 style={{ fontFamily:"Syne,sans-serif", fontSize:18, fontWeight:800, color:"#3D6B6B", margin:0, textAlign:"center" }}>
            Générateur de mot de passe
          </h3>
          <button onClick={onClose} style={{ position:"absolute", top:14, right:16, background:"none", border:"none", fontSize:16, color:"var(--text-3)", cursor:"pointer" }}>
            <i className="bi bi-x-lg"/>
          </button>
        </div>

        {/* Corps */}
        <div style={{ padding:"20px 24px" }}>

          {/* Champ résultat */}
          <div style={{ position:"relative", marginBottom:12 }}>
            <input readOnly value={password} placeholder="Cliquez sur Créer pour générer…"
              style={{ width:"100%", padding:"11px 90px 11px 14px", border:"2px solid #3D6B6B", borderRadius:10, fontSize:15, fontFamily:"monospace", fontWeight:600, color:"var(--text-1)", background:"rgba(61,107,107,.12)", outline:"none", boxSizing:"border-box", letterSpacing: password ? ".05em" : "normal" }}
            />
            <div style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", display:"flex", gap:4 }}>
              <button onClick={handleCopy} title="Copier"
                style={{ background:"none", border:"none", fontSize:16, color: copied ? "#3D6B6B" : "#aaa", cursor:"pointer", padding:4 }}>
                <i className={`bi bi-${copied ? "check2" : "copy"}`}/>
              </button>
              <button onClick={generate} title="Régénérer" disabled={loading}
                style={{ background:"none", border:"none", fontSize:16, color:"#aaa", cursor:"pointer", padding:4 }}>
                <i className={`bi bi-arrow-repeat${loading ? " spin" : ""}`}/>
              </button>
            </div>
          </div>

          {error && <div style={{ fontSize:12, color:"#fa5252", marginBottom:10 }}><i className="bi bi-exclamation-triangle-fill me-1"/>{error}</div>}

          {/* Indicateur de force */}
          <div style={{ marginBottom:16 }}>
            <div style={{ display:"flex", gap:4, marginBottom:5 }}>
              {[1,2,3,4,5].map(i => (
                <div key={i} style={{ flex:1, height:5, borderRadius:99, background: password && i <= strength.score ? strength.color : "var(--navy-soft)", transition:"background .25s" }}/>
              ))}
            </div>
            {password && (
              <div style={{ fontSize:13, color:"#555" }}>
                <strong style={{ color:"var(--text-1)" }}>Force du mot de passe:</strong>{" "}
                <span style={{ color:strength.color, fontWeight:600 }}>{strength.label}</span>
              </div>
            )}
          </div>

          {/* Slider longueur */}
          <div style={{ marginBottom:20, padding:"14px 16px", background:"var(--navy-mid)", borderRadius:10, border:"1px solid var(--border)" }}>
            <div style={{ position:"relative", marginBottom:8 }}>
              <input type="range" min={8} max={32} value={length}
                onChange={e => setLength(Number(e.target.value))}
                style={{ width:"100%", accentColor:"#3D6B6B", cursor:"pointer" }}
              />
              <div style={{
                position:"absolute",
                left:`${((length - 8) / (32 - 8)) * 100}%`,
                top:-30, transform:"translateX(-50%)",
                background:"#3D6B6B", color:"#fff",
                borderRadius:"50%", width:28, height:28,
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:11, fontWeight:700, pointerEvents:"none",
              }}>
                {length}
              </div>
            </div>
            <div style={{ textAlign:"center", fontSize:13, color:"#888", marginTop:4 }}>
              Longueur du mot de passe
            </div>
          </div>

          {/* Toggles */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px 24px", marginBottom:24, padding:"14px 16px", background:"var(--navy-mid)", borderRadius:10, border:"1px solid var(--border)" }}>
            <Toggle checked={useLower}   onChange={setUseLower}   label="Lettres"    />
            <Toggle checked={useNumbers} onChange={setUseNumbers} label="Numéros"    />
            <Toggle checked={useUpper}   onChange={setUseUpper}   label="Majuscules" />
            <Toggle checked={useSymbols} onChange={setUseSymbols} label="Symboles"   />
          </div>

          {/* Boutons Créer + Copier */}
          <div style={{ display:"flex", gap:10 }}>
            <button onClick={generate} disabled={loading}
              style={{ flex:1, padding:"11px 0", borderRadius:10, border:"2px solid #3D6B6B", background:"var(--card-bg)", color:"#3D6B6B", fontSize:14, fontWeight:600, cursor: loading ? "not-allowed" : "pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:6, transition:"all .15s" }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(61,107,107,.12)"}
              onMouseLeave={e => e.currentTarget.style.background = "var(--card-bg)"}
            >
              {loading ? <><i className="bi bi-arrow-repeat spin"/> Génération…</> : <><i className="bi bi-magic"/> Créer</>}
            </button>

            <button onClick={handleCopy} disabled={!password}
              style={{ flex:1, padding:"11px 0", borderRadius:10, border:"none", background: password ? "#3D6B6B" : "var(--navy-soft)", color: password ? "#fff" : "#aaa", fontSize:14, fontWeight:600, cursor: password ? "pointer" : "not-allowed", display:"flex", alignItems:"center", justifyContent:"center", gap:6, transition:"all .15s" }}
            >
              {copied ? <><i className="bi bi-check2"/> Copié !</> : <><i className="bi bi-copy"/> Copier</>}
            </button>
          </div>

          {/* Bouton Utiliser — seulement si appelé depuis un champ */}
          {onUse && password && (
            <button onClick={handleUse}
              style={{ width:"100%", marginTop:10, padding:"10px 0", borderRadius:10, border:"none", background:"rgba(61,107,107,.1)", color:"#3D6B6B", fontSize:13, fontWeight:600, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(61,107,107,.18)"}
              onMouseLeave={e => e.currentTarget.style.background = "rgba(61,107,107,.1)"}
            >
              <i className="bi bi-arrow-down-circle"/> Utiliser ce mot de passe
            </button>
          )}

        </div>
      </div>

      <style>{`
        @keyframes pgSlideUp { from{transform:translateY(24px);opacity:0} to{transform:none;opacity:1} }
        @keyframes spin { to{transform:rotate(360deg)} }
        .spin { animation:spin .7s linear infinite; display:inline-block }
      `}</style>
    </div>
  );
}